import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  CreditCard,
  ExternalLink,
  Gift,
  RefreshCw,
  Save,
  ScrollText,
  Sparkles,
  User,
  Wallet,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../services/api';

const DEFAULT_SETTINGS = {
  deposit_rules: {
    min_amount: 10,
    max_amount: 10000,
    enabled: true,
    first_deposit_bonus_percent: 100,
    regular_bonus_percent: 20,
  },
  withdrawal_rules: {
    min_amount: 20,
    max_amount: 5000,
    max_total_per_day: 0,
    enabled: true,
    fee_percent: 0,
    min_balance_after: 0,
  },
  load_rules: { min_xp: 10, max_xp: 100000, enabled: true },
  redeem_rules: { min_provider_amount: 1, max_provider_amount: 10000, enabled: true },
  bonus_rules: {
    first_deposit_bonus_pct: 100,
    signup_bonus_xp: 50,
    referral_bonus_xp: 25,
    enabled: true,
  },
  referral_rules: {
    player_referrer_bonus_sc: 25,
    player_referred_bonus_sc: 10,
    player_referrer_load_percent: 0,
    staff_referral_enabled: true,
    player_referral_enabled: true,
  },
  xp_conversion: {
    website_currency: 'USD',
    provider_currency: 'USD',
    load_xp_per_provider_unit: 1,
    redeem_xp_per_provider_unit: 1,
  },
};

const DEFAULT_CASHOUT = {
  headline: 'Cashout Rules',
  intro: '',
  footer: '',
  image_url: null,
  min_game_load: 5,
  max_game_load: 5000,
  min_game_withdraw: 0,
  max_game_withdraw: 0,
  min_redeem_multiplier: 0,
  max_redeem_multiplier: 10,
  rules: [],
};

const DEFAULT_FREEPLAY = {
  ...DEFAULT_CASHOUT,
  headline: 'Freeplay Cashout Rules',
  intro: 'Freeplay loads use bonus credits, so cashout rules can be different.',
  min_game_load: 1,
};

const mergeSettings = (incoming = {}) => ({
  ...DEFAULT_SETTINGS,
  ...incoming,
  deposit_rules: { ...DEFAULT_SETTINGS.deposit_rules, ...(incoming.deposit_rules || {}) },
  withdrawal_rules: { ...DEFAULT_SETTINGS.withdrawal_rules, ...(incoming.withdrawal_rules || {}) },
  load_rules: { ...DEFAULT_SETTINGS.load_rules, ...(incoming.load_rules || {}) },
  redeem_rules: { ...DEFAULT_SETTINGS.redeem_rules, ...(incoming.redeem_rules || {}) },
  bonus_rules: { ...DEFAULT_SETTINGS.bonus_rules, ...(incoming.bonus_rules || {}) },
  referral_rules: { ...DEFAULT_SETTINGS.referral_rules, ...(incoming.referral_rules || {}) },
  xp_conversion: { ...DEFAULT_SETTINGS.xp_conversion, ...(incoming.xp_conversion || {}) },
});

const normalizeCashout = (doc = {}) => ({
  ...DEFAULT_CASHOUT,
  ...doc,
  freeplay: { ...DEFAULT_FREEPLAY, ...(doc.freeplay || {}) },
  rules: Array.isArray(doc.rules) ? doc.rules : [],
});

const numberValue = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const cashoutPayload = (variant) => ({
  headline: variant.headline || 'Cashout Rules',
  intro: variant.intro || '',
  footer: variant.footer || '',
  image_url: variant.image_url || null,
  min_game_load: numberValue(variant.min_game_load),
  max_game_load: numberValue(variant.max_game_load),
  min_game_withdraw: numberValue(variant.min_game_withdraw),
  max_game_withdraw: numberValue(variant.max_game_withdraw),
  min_redeem_multiplier: numberValue(variant.min_redeem_multiplier),
  max_redeem_multiplier: numberValue(variant.max_redeem_multiplier),
  rules: Array.isArray(variant.rules)
    ? variant.rules.map((r) => ({
        icon: r.icon || '',
        title: r.title || '',
        body: r.body || '',
        image_url: r.image_url || null,
      }))
    : [],
});

const money = (value) => `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const pct = (value) => `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;

export default function PlatformRulesPage({ onNavigate }) {
  const [settings, setSettings] = useState(mergeSettings());
  const [cashoutRules, setCashoutRules] = useState(normalizeCashout());
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [referralTiers, setReferralTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingCashout, setSavingCashout] = useState(false);
  const [savingMethodId, setSavingMethodId] = useState(null);

  const enabledMethods = useMemo(
    () => paymentMethods.filter((m) => m.enabled !== false),
    [paymentMethods]
  );

  const loadAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, cashoutRes, methodsRes, tiersRes] = await Promise.all([
        api.get('/api/admin/settings').catch(() => ({ data: {} })),
        api.get('/api/admin/cashout-rules').catch(() => ({ data: {} })),
        api.get('/api/admin/payment-methods').catch(() => ({ data: { items: [] } })),
        api.get('/api/admin/referral-tiers').catch(() => null),
      ]);

      setSettings(mergeSettings(settingsRes.data?.settings || settingsRes.data || {}));
      setCashoutRules(normalizeCashout(cashoutRes.data || {}));
      setPaymentMethods(methodsRes.data?.items || methodsRes.data?.methods || []);
      setReferralTiers(tiersRes?.data?.data?.items || tiersRes?.data?.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const updateSettings = (section, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [key]: value,
      },
    }));
  };

  const updateCashout = (source, key, value) => {
    setCashoutRules((prev) => {
      const current = normalizeCashout(prev);
      if (source === 'freeplay') {
        return {
          ...current,
          freeplay: {
            ...current.freeplay,
            [key]: value,
          },
        };
      }
      return {
        ...current,
        [key]: value,
      };
    });
  };

  const updatePaymentMethodRule = (methodId, key, value) => {
    setPaymentMethods((prev) => prev.map((method) => {
      if (method.id !== methodId) return method;
      const next = { ...method, [key]: value };
      if (key === 'bonus_percent') {
        next.bonus_or_fee = Number(value) || 0;
        next.bonus_fee_direction = (Number(value) || 0) > 0 ? 'bonus' : next.bonus_fee_direction;
      }
      if (key === 'fee_percent') {
        next.bonus_or_fee = Number(value) || 0;
        next.bonus_fee_direction = (Number(value) || 0) > 0 ? 'fee' : next.bonus_fee_direction;
      }
      return next;
    }));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/api/admin/settings', settings);
      toast.success('Platform rules saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Could not save platform rules');
    } finally {
      setSavingSettings(false);
    }
  };

  const saveCashoutRules = async () => {
    const main = normalizeCashout(cashoutRules);
    const freeplay = main.freeplay || DEFAULT_FREEPLAY;
    setSavingCashout(true);
    try {
      await api.put('/api/admin/cashout-rules', {
        ...cashoutPayload(main),
        freeplay: cashoutPayload(freeplay),
      });
      toast.success('Cashout and freeplay rules saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Could not save cashout rules');
    } finally {
      setSavingCashout(false);
    }
  };

  const savePaymentMethodRule = async (method) => {
    if (!method?.id || method.readonly) return;
    const bonus = numberValue(method.bonus_percent);
    const fee = numberValue(method.fee_percent);
    if (method.flow === 'both' && (bonus > 0 || fee > 0)) {
      toast.error('Bonus/Fee requires Deposit Only or Withdrawal Only method. Split this method first.');
      return;
    }
    setSavingMethodId(method.id);
    try {
      await api.patch(`/api/admin/payment-methods/${method.id}`, {
        min_amount: numberValue(method.min_amount),
        max_amount: numberValue(method.max_amount),
        bonus_percent: bonus,
        fee_percent: fee,
        bonus_or_fee: fee > 0 ? fee : bonus,
        bonus_fee_direction: fee > 0 ? 'fee' : 'bonus',
      });
      toast.success(`${method.title || 'Payment method'} rules saved`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Could not save payment method rules');
    } finally {
      setSavingMethodId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="platform-rules-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Platform Rules</h2>
          <p className="text-sm text-gray-400">
            One place for cashout, freeplay, bonus, referral, deposit, load, and redeem rules.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadAll} className="bg-[#131822] border border-white/10 text-white hover:bg-white/5">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={saveSettings} disabled={savingSettings} className="bg-[#D4AF37] text-black hover:bg-[#F5D76E]">
            <Save className="w-4 h-4 mr-2" /> {savingSettings ? 'Saving...' : 'Save Platform'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-4">
        <SummaryCard icon={Wallet} label="Deposit Range" value={`${money(settings.deposit_rules.min_amount)} - ${money(settings.deposit_rules.max_amount)}`} />
        <SummaryCard icon={ArrowUpCircle} label="Withdrawal Range" value={`${money(settings.withdrawal_rules.min_amount)} - ${money(settings.withdrawal_rules.max_amount)}`} />
        <SummaryCard icon={Sparkles} label="Signup Bonus" value={`${settings.bonus_rules.signup_bonus_xp || 0} FP`} />
        <SummaryCard icon={CreditCard} label="Payment Methods" value={`${enabledMethods.length} active`} />
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <RulePanel
          icon={ArrowDownCircle}
          title="Deposit Rules"
          description="Controls player cash-in limits and deposit bonus defaults."
          action={<Toggle checked={settings.deposit_rules.enabled} onChange={(v) => updateSettings('deposit_rules', 'enabled', v)} />}
        >
          <RuleGrid>
            <NumberField label="Min deposit" value={settings.deposit_rules.min_amount} onChange={(v) => updateSettings('deposit_rules', 'min_amount', v)} />
            <NumberField label="Max deposit" value={settings.deposit_rules.max_amount} onChange={(v) => updateSettings('deposit_rules', 'max_amount', v)} />
            <NumberField label="First deposit bonus %" value={settings.deposit_rules.first_deposit_bonus_percent} onChange={(v) => updateSettings('deposit_rules', 'first_deposit_bonus_percent', v)} />
            <NumberField label="Regular deposit bonus %" value={settings.deposit_rules.regular_bonus_percent} onChange={(v) => updateSettings('deposit_rules', 'regular_bonus_percent', v)} />
          </RuleGrid>
        </RulePanel>

        <RulePanel
          icon={ArrowUpCircle}
          title="Wallet Withdrawal Rules"
          description="Controls player wallet cashout limits before method-specific details."
          action={<Toggle checked={settings.withdrawal_rules.enabled} onChange={(v) => updateSettings('withdrawal_rules', 'enabled', v)} />}
        >
          <RuleGrid>
            <NumberField label="Min withdrawal" value={settings.withdrawal_rules.min_amount} onChange={(v) => updateSettings('withdrawal_rules', 'min_amount', v)} />
            <NumberField label="Max withdrawal" value={settings.withdrawal_rules.max_amount} onChange={(v) => updateSettings('withdrawal_rules', 'max_amount', v)} />
            <NumberField label="Max withdrawal per day" value={settings.withdrawal_rules.max_total_per_day} onChange={(v) => updateSettings('withdrawal_rules', 'max_total_per_day', v)} />
            <NumberField label="Withdrawal fee %" value={settings.withdrawal_rules.fee_percent} onChange={(v) => updateSettings('withdrawal_rules', 'fee_percent', v)} />
            <NumberField label="Min balance after" value={settings.withdrawal_rules.min_balance_after} onChange={(v) => updateSettings('withdrawal_rules', 'min_balance_after', v)} />
          </RuleGrid>
          <p className="mt-3 text-xs text-gray-500">Set max withdrawal per day to 0 for no daily cap.</p>
        </RulePanel>

        <RulePanel
          icon={Gift}
          title="Bonuses & Freeplay Rules"
          description="Signup, first deposit, and referral freeplay amounts."
          action={<Toggle checked={settings.bonus_rules.enabled} onChange={(v) => updateSettings('bonus_rules', 'enabled', v)} />}
        >
          <RuleGrid>
            <NumberField label="First deposit bonus %" value={settings.bonus_rules.first_deposit_bonus_pct} onChange={(v) => updateSettings('bonus_rules', 'first_deposit_bonus_pct', v)} />
            <NumberField label="Signup bonus FP" value={settings.bonus_rules.signup_bonus_xp} onChange={(v) => updateSettings('bonus_rules', 'signup_bonus_xp', v)} />
            <NumberField label="Referral bonus FP" value={settings.bonus_rules.referral_bonus_xp} onChange={(v) => updateSettings('bonus_rules', 'referral_bonus_xp', v)} />
          </RuleGrid>
        </RulePanel>

        <RulePanel
          icon={User}
          title="Referral Load Rules"
          description="Player referral earning rules and staff referral switch."
          action={<Toggle checked={settings.referral_rules.player_referral_enabled} onChange={(v) => updateSettings('referral_rules', 'player_referral_enabled', v)} />}
        >
          <RuleGrid>
            <NumberField label="Referrer earns FP" value={settings.referral_rules.player_referrer_bonus_sc} onChange={(v) => updateSettings('referral_rules', 'player_referrer_bonus_sc', v)} />
            <NumberField label="Referred player earns FP" value={settings.referral_rules.player_referred_bonus_sc} onChange={(v) => updateSettings('referral_rules', 'player_referred_bonus_sc', v)} />
            <NumberField label="Referrer earns wallet % of load" value={settings.referral_rules.player_referrer_load_percent} onChange={(v) => updateSettings('referral_rules', 'player_referrer_load_percent', v)} />
          </RuleGrid>
          <div className="mt-4 flex flex-wrap gap-2">
            <TogglePill label="Player referrals" checked={settings.referral_rules.player_referral_enabled} onChange={(v) => updateSettings('referral_rules', 'player_referral_enabled', v)} />
            <TogglePill label="Staff referrals" checked={settings.referral_rules.staff_referral_enabled} onChange={(v) => updateSettings('referral_rules', 'staff_referral_enabled', v)} />
          </div>
          {referralTiers.length > 0 && (
            <div className="mt-4 rounded-xl border border-white/10 bg-[#0B0E14]/70 p-3">
              <p className="mb-2 text-xs uppercase tracking-[0.18em] text-gray-500">Commission tiers</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {referralTiers.slice(0, 6).map((tier) => (
                  <div key={tier.name || tier.tier} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                    <div className="text-sm font-semibold text-white">{tier.name || tier.tier}</div>
                    <div className="text-xs text-gray-400">
                      {tier.min_referrals || 0}+ refs - L1 {pct(tier.l1_commission_percent ?? tier.commission_percent)} - L2 {pct(tier.l2_commission_percent)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </RulePanel>

        <RulePanel
          icon={CreditCard}
          title="Payment Method Bonuses & Fees"
          description="Set method-level min, max, bonus, and fee here only. Payment API is only for adding gateways and manual payment details."
          action={
            <button
              type="button"
              onClick={() => onNavigate?.('payments')}
              className="inline-flex items-center gap-1 rounded-lg border border-[#D4AF37]/40 px-3 py-1.5 text-xs font-semibold text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              Open Payments <ExternalLink className="h-3.5 w-3.5" />
            </button>
          }
        >
          <div className="space-y-3">
            {paymentMethods.length === 0 ? (
              <EmptyLine text="No payment rules configured yet." />
            ) : paymentMethods.slice(0, 8).map((method) => (
              <div key={method.id} className="rounded-xl border border-white/10 bg-[#0B0E14]/70 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-white">{method.title || method.name || 'Payment Method'}</div>
                    <div className="text-xs text-gray-500 capitalize">{method.payment_mode || 'manual'} - {method.flow || 'both'}</div>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-[10px] font-bold ${method.enabled !== false ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>
                    {method.enabled !== false ? 'Active' : 'Disabled'}
                  </span>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-4">
                  <NumberField label="Min" value={method.min_amount} onChange={(v) => updatePaymentMethodRule(method.id, 'min_amount', v)} disabled={method.readonly} />
                  <NumberField label="Max" value={method.max_amount} onChange={(v) => updatePaymentMethodRule(method.id, 'max_amount', v)} disabled={method.readonly} />
                  <NumberField label="Bonus %" value={method.bonus_percent ?? method.bonus_or_fee ?? 0} onChange={(v) => updatePaymentMethodRule(method.id, 'bonus_percent', v)} disabled={method.readonly} />
                  <NumberField label="Fee %" value={method.fee_percent} onChange={(v) => updatePaymentMethodRule(method.id, 'fee_percent', v)} disabled={method.readonly} />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <p className="text-[11px] text-gray-500">
                    {method.readonly ? 'Global method rules are view-only for this tenant.' : 'Saved from Platform Rules only.'}
                  </p>
                  {!method.readonly && (
                    <Button
                      type="button"
                      onClick={() => savePaymentMethodRule(method)}
                      disabled={savingMethodId === method.id}
                      className="h-8 rounded-lg bg-[#D4AF37] px-3 text-xs text-black hover:bg-[#F5D76E]"
                    >
                      <Save className="mr-1.5 h-3.5 w-3.5" />
                      {savingMethodId === method.id ? 'Saving...' : 'Save'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </RulePanel>
      </div>

      <RulePanel
        icon={ScrollText}
        title="Cashout Rules: Main Balance & Freeplay"
        description="These are the rules players see from game cards. Main balance uses multiplier rules; freeplay can use amount-based limits."
        action={
          <Button onClick={saveCashoutRules} disabled={savingCashout} className="bg-[#D4AF37] text-black hover:bg-[#F5D76E]">
            <Save className="w-4 h-4 mr-2" /> {savingCashout ? 'Saving...' : 'Save Cashout Rules'}
          </Button>
        }
      >
        <div className="grid gap-4 xl:grid-cols-2">
          <CashoutVariant
            title="Main Balance Game Rules"
            source="main"
            rules={cashoutRules}
            onChange={updateCashout}
          />
          <CashoutVariant
            title="Freeplay Game Rules"
            source="freeplay"
            rules={cashoutRules.freeplay || DEFAULT_FREEPLAY}
            onChange={updateCashout}
          />
        </div>
      </RulePanel>
    </div>
  );
}

function RulePanel({ icon: Icon, title, description, action, children }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-[#131822]/90 p-5 shadow-xl shadow-black/20">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{title}</h3>
            <p className="text-sm text-gray-400">{description}</p>
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function RuleGrid({ children }) {
  return <div className="grid gap-3 sm:grid-cols-2">{children}</div>;
}

function NumberField({ label, value, onChange, disabled = false }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">{label}</span>
      <Input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        disabled={disabled}
        className="bg-[#0B0E14] border-white/10 text-white disabled:cursor-not-allowed disabled:opacity-60"
      />
    </label>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`h-7 w-14 rounded-full border p-1 transition ${checked ? 'border-emerald-400/50 bg-emerald-500/30' : 'border-white/10 bg-white/5'}`}
      aria-pressed={checked}
    >
      <span className={`block h-5 w-5 rounded-full bg-white transition ${checked ? 'translate-x-7' : 'translate-x-0'}`} />
    </button>
  );
}

function TogglePill({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${checked ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-white/10 bg-white/5 text-gray-400'}`}
    >
      {label}: {checked ? 'On' : 'Off'}
    </button>
  );
}

function SummaryCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#131822]/90 p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#D4AF37]/10 text-[#D4AF37]">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.16em] text-gray-500">{label}</p>
          <p className="truncate text-lg font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

function EmptyLine({ text }) {
  return (
    <div className="rounded-xl border border-dashed border-white/10 bg-[#0B0E14]/60 p-4 text-sm text-gray-500">
      {text}
    </div>
  );
}

function CashoutVariant({ title, source, rules, onChange }) {
  const variant = source === 'freeplay' ? { ...DEFAULT_FREEPLAY, ...(rules || {}) } : { ...DEFAULT_CASHOUT, ...(rules || {}) };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0B0E14]/70 p-4">
      <div className="mb-4">
        <h4 className="font-semibold text-white">{title}</h4>
        <p className="text-xs text-gray-500">{variant.headline}</p>
      </div>
      <RuleGrid>
        <NumberField label="Min add credits" value={variant.min_game_load} onChange={(v) => onChange(source, 'min_game_load', v)} />
        <NumberField label="Max add credits" value={variant.max_game_load} onChange={(v) => onChange(source, 'max_game_load', v)} />
        <NumberField label="Min withdraw credits" value={variant.min_game_withdraw} onChange={(v) => onChange(source, 'min_game_withdraw', v)} />
        <NumberField label="Max withdraw credits" value={variant.max_game_withdraw} onChange={(v) => onChange(source, 'max_game_withdraw', v)} />
        <NumberField label="Min withdrawal multiplier" value={variant.min_redeem_multiplier} onChange={(v) => onChange(source, 'min_redeem_multiplier', v)} />
        <NumberField label="Max withdrawal multiplier" value={variant.max_redeem_multiplier} onChange={(v) => onChange(source, 'max_redeem_multiplier', v)} />
      </RuleGrid>
    </div>
  );
}
